/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Component for custom service type field
 */
import { useMemo } from 'react';
import _ from 'lodash';
import { Button, Col, Row, Select, SelectProps, Tag, Tooltip } from 'antd';
import { Info } from '@icon-park/react';
import { fieldsConfigMapping } from 'components/forms/schema/eligibilityRequestFormSchema';
import { sortedServiceTypes } from 'utils/eligibility/eligibilityRequestForm';

export interface IProps extends SelectProps {
	value: string[] | undefined;
	eligibilityMeta: Payers.IEligibilityMeta;
	options: IMultiSelectOption[];
	onChange: (value: string[] | undefined) => void;
}

const ServiceTypeDropdown: React.FC<IProps> = ({ value, options, eligibilityMeta, onChange, ...restProps }) => {
	const serviceTypeConfig = _.get(eligibilityMeta?.fieldConfig, fieldsConfigMapping.serviceType);
	const isMultiSelect = serviceTypeConfig?.max_items !== 1;
	const bindedServiceTypeCodes = _.map(
		_.filter(serviceTypeConfig?.custom_config?.conditions, (condition) => !!condition?.binding),
		'code',
	);

	const serviceTypeEnums = _.get(eligibilityMeta?.fieldEnums, fieldsConfigMapping.serviceType);
	const filteredOptions = !_.isEmpty(serviceTypeEnums)
		? _.filter(options, (option) => _.includes(serviceTypeEnums, option.value))
		: options;

	// custom tag renderer
	const tagRender = (tag: AnyType) => {
		const { label, value: selectedServiceType, onClose } = tag;
		const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
			event.preventDefault();
			event.stopPropagation();
		};

		return (
			<Tag
				closable={!_.includes(bindedServiceTypeCodes, selectedServiceType) && _.isFunction(onClose)}
				onClose={onClose}
				onMouseDown={onPreventMouseDown}
				style={{ display: 'flex', justifyContent: 'center' }}
			>
				{label}
			</Tag>
		);
	};

	// if single select option, return string value else return array for multiselect
	const getValue = () => {
		if (!isMultiSelect && value && !_.isEmpty(value)) {
			return value[0];
		}
		return value;
	};

	const changeValue = (newValue: string | string[]) => {
		if (_.isString(newValue)) {
			onChange([newValue]);
		} else {
			onChange(newValue);
		}
	};

	// prepare select options with service type included descriptions
	const selectOptions = useMemo(() => {
		const sortedOptions = _.sortBy(sortedServiceTypes(filteredOptions), (option) => (_.includes(value, option.value) ? 1 : 2));
		return _.map(sortedOptions, (option) => {
			const includedServices = (
				_.find(serviceTypeConfig?.custom_config?.conditions, {
					code: option.value,
				}) as Payers.IServiceTypeConfig
			)?.includes;
			const includedServiceOptions = _.compact(
				_.map(includedServices, (serviceCode) => {
					return _.find(options, { value: serviceCode });
				}),
			);

			return (
				<Select.Option
					disabled={isMultiSelect && (option.disabled || _.includes(bindedServiceTypeCodes, option.value))}
					key={option.value}
					value={option.value}
					label={option.label}
					title={option.label}
				>
					<Row style={{ display: 'flex' }}>
						<Col
							span={!_.isEmpty(includedServices) ? 22 : 24}
							style={{ margin: 'auto 0px', overflow: 'hidden', textOverflow: 'ellipsis', flex: 'unset' }}
						>
							{option.label}
						</Col>
						{!_.isEmpty(includedServices) && (
							<Col span={2}>
								<Tooltip
									classNames={{ root: 'service-type-tooltip' }}
									title={
										<>
											<div style={{ fontSize: 16, fontWeight: 500, color: '#000000e0' }}>{'Includes:'}</div>
											<ul style={{ margin: 0, paddingLeft: 20 }}>
												{_.map(sortedServiceTypes(includedServiceOptions), (serviceOption) => {
													return (
														<li style={{ color: '#000000e0' }} key={serviceOption.value}>
															{serviceOption?.label}
														</li>
													);
												})}
											</ul>
										</>
									}
								>
									<Button type='text' shape='circle' style={{ height: 'fit-content' }} icon={<Info fill='#8c8c8c' />} />
								</Tooltip>
							</Col>
						)}
					</Row>
				</Select.Option>
			);
		});
	}, [filteredOptions, serviceTypeConfig, value]);

	return (
		<Select
			{...restProps}
			styles={{ popup: { root: { minWidth: 500 } } }}
			showSearch={true}
			allowClear={false}
			optionFilterProp='label'
			maxTagCount='responsive'
			mode={isMultiSelect ? 'multiple' : undefined}
			value={getValue()}
			onChange={changeValue}
			tagRender={isMultiSelect ? tagRender : undefined}
		>
			{selectOptions}
		</Select>
	);
};
export default ServiceTypeDropdown;
